import {CSSProperties, useEffect, useState} from 'react';

interface IReportProgressBarProps {
    current: number;
    total: number;
    onVisibilityChange?: (visible: boolean) => void;
}

const containerStyle: CSSProperties = {
    position: 'fixed',
    top: 48,
    left: 0,
    right: 0,
    zIndex: 999,
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
};

const printHideStyle = `
@media print {
    .report-progress-bar {
        display: none !important;
    }
}
`;

const progressBarBgStyle: CSSProperties = {
    width: '100%',
    height: '4px',
    backgroundColor: '#e0e0e0',
};

const progressBarFillStyle = (percentage: number): CSSProperties => ({
    height: '100%',
    backgroundColor: '#0f62fe',
    transition: 'width 0.3s ease-in-out',
    width: `${percentage}%`,
});

const textContainerStyle = (isComplete: boolean): CSSProperties => ({
    padding: isComplete ? '8px 16px 8px' : '0px 16px 8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 500,
});

const noteStyle: CSSProperties = {
    padding: '8px 16px',
    fontSize: '11px',
    color: '#f57c00',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
};

const ReportProgressBar = (props: IReportProgressBarProps) => {
    const {current, total, onVisibilityChange} = props;
    const [isVisible, setIsVisible] = useState(true);

    const percentage = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
    const isComplete = current >= total && total > 0;

    // Auto-hide after completion
    useEffect(() => {
        if (isComplete) {
            const timer = setTimeout(() => {
                setIsVisible(false);
                onVisibilityChange?.(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [isComplete, onVisibilityChange]);

    // Notify parent of initial visibility
    useEffect(() => {
        onVisibilityChange?.(true);
    }, []);

    if (!isVisible) {
        return null;
    }

    const currentTextContainerStyle = textContainerStyle(isComplete);

    return (
        <>
            <style>{printHideStyle}</style>
            <div className="report-progress-bar" style={containerStyle}>
                {!isComplete && (
                    <div style={noteStyle}>
                        <span>⚠️ This report may take a few minutes to load. Please keep this tab visible until all maps finish loading.</span>
                    </div>
                )}
                <div style={currentTextContainerStyle}>
                    <span>
                        {isComplete ? '✓ Report Ready' : 'Generating Report...'}
                    </span>
                    <span>
                        {percentage}%
                    </span>
                </div>
                <div style={progressBarBgStyle}>
                    <div style={progressBarFillStyle(percentage)} />
                </div>
            </div>
        </>
    );
};

export default ReportProgressBar;
